import * as THREE from 'three';
import { World } from '../worlds/World';
import * as ObjectCreation from '../ObjectCreation';
import * as TextCreation from '../TextCreation';
import { OrbFactory } from '../../../orbs/OrbFactory';
import { registerScenePolicy } from '../../../core/ScenePolicies';
import { computeRingLayout } from '../../../utils/RingLayout';
function addConceptOrbs(scene) {
    const concepts = [
        { title: 'Phenomenology', description: 'Intentional structures of lived experience.' },
        { title: 'Ontology', description: 'What fundamentally exists and how it is categorized.' },
        { title: 'Epistemology', description: 'Foundations and limits of knowledge.' },
        { title: 'Ethics', description: 'Inquiry into the good, virtue, and moral reasoning.' },
        { title: 'Logic', description: 'Validity, inference, and formal consequence.' },
        { title: 'Aesthetics', description: 'Beauty, taste, and artistic value.' },
        { title: 'Metaphysics', description: 'Beyond the physical: being, causation, modality.' },
        { title: 'Existentialism', description: 'Freedom, authenticity, and human choice.' },
    ];
    const centre = new THREE.Vector3(0, 420, 0);
    const items = concepts.map((concept, index) => ({ id: `${concept.title}-${index}`, radius: 240 }));
    const layout = computeRingLayout(items, {
        centre,
        minRadius: 2000,
        minSeparation: 240,
        startAngle: Math.PI / 2,
    });
    layout.placements.forEach((placement, index) => {
        const concept = concepts[index];
        if (!concept)
            return;
        const orb = OrbFactory.createOrbByType('TEXT_OBJECT_ORB', concept.title, {
            concept: concept.title,
            summary: concept.description,
        });
        orb.setPosition(placement.position.clone());
        orb.addToScene(scene);
    });
}
function addInnerMeditationRing(scene) {
    const meditations = [
        { title: 'Dialectic', description: 'Dialogue-driven synthesis of perspectives.' },
        { title: 'Absurdism', description: 'Meaning through confrontation with the absurd.' },
        { title: 'Stoicism', description: 'Virtue via rational mastery of response.' },
        { title: 'Hermeneutics', description: 'Interpretation, context, and understanding.' },
    ];
    const centre = new THREE.Vector3(0, 380, 0);
    const items = meditations.map((concept, index) => ({ id: `${concept.title}-inner-${index}`, radius: 200 }));
    const layout = computeRingLayout(items, {
        centre,
        minRadius: 1100,
        minSeparation: 200,
        startAngle: Math.PI / 4,
    });
    layout.placements.forEach((placement, index) => {
        const concept = meditations[index];
        if (!concept)
            return;
        const orb = OrbFactory.createOrbByType('PROMPT_ORB', concept.title);
        orb.setPosition(placement.position.clone());
        orb.jsonData = { insight: concept.description };
        orb.addToScene(scene);
    });
}
function buildPhilosophyWorld(context) {
    const { scene } = context;
    scene.background = new THREE.Color(0xfffbf4);
    scene.fog = new THREE.Fog(0xffeedb, 0, 26000);
    const { mirror, tintOverlay } = ObjectCreation.createMirrorFloor(1600, {
        baseColor: 0xf2d9c4,
        y: 0.05,
        dpr: 1.5,
        textureScale: 0.9,
        clipBias: 0.002,
    });
    const philosophyTint = tintOverlay.material;
    philosophyTint.opacity = 0.26;
    philosophyTint.color = new THREE.Color(0xffb27a);
    scene.add(mirror);
    scene.add(tintOverlay);
    const ambient = new THREE.HemisphereLight(0xfff2d6, 0xffc7a1, 0.85);
    const ember = new THREE.PointLight(0xffad66, 1.0, 2800, 1.4);
    ember.position.set(0, 820, 0);
    const rim = new THREE.DirectionalLight(0xffffff, 0.5);
    rim.position.set(-2200, 2600, -1600);
    const fill = new THREE.DirectionalLight(0xffdfb3, 0.35);
    fill.position.set(2200, 2400, 1600);
    scene.add(ambient);
    scene.add(ember);
    scene.add(rim);
    scene.add(fill);
    //scene.add(ObjectCreation.createWorldAxis(20000, 20000, 1000))
    const philosophyAxis = ObjectCreation.createWorldAxis(20000, 20000, 1000, {
        showGrid: true,
        gridPrimaryColor: 0xffdec1,
        gridSecondaryColor: 0xffc9a4,
        gridOpacity: 0.28,
        gridYOffset: 0.05,
        // softer axis to match the warm palette if you want:
        axisColor: 0xffc9a4,
        verticalOffset: 0.01,
    });
    scene.add(philosophyAxis);
    const title = TextCreation.createTextSprite('Philosophy Atrium', 3.2, new THREE.Vector3(0, 560, -760));
    const subtitle = TextCreation.createTextSprite('Conceptual constellations & narratives', 1.7, new THREE.Vector3(0, 460, -760));
    scene.add(title);
    scene.add(subtitle);
    addConceptOrbs(scene);
    addInnerMeditationRing(scene);
    const ribbon = new THREE.Line(new THREE.BufferGeometry().setFromPoints(Array.from({ length: 180 }, (_, i) => {
        const t = i / 20;
        return new THREE.Vector3(Math.cos(t) * (800 + Math.sin(t * 0.3) * 140), 320 + Math.sin(t * 0.5) * 120, Math.sin(t) * (800 + Math.cos(t * 0.4) * 140));
    })), new THREE.LineBasicMaterial({ color: 0xffd1a3, transparent: true, opacity: 0.45 }));
    ribbon.position.y = -40;
    scene.add(ribbon);
    registerScenePolicy('philosophy', { floorY: 2 });
}
export const PhilosophyWorld = new World({
    slug: 'philosophy',
    label: 'Philosophy',
    sceneName: 'philosophy',
    build: buildPhilosophyWorld,
});
//global.worldRegistry.registerWorld(PhilosophyWorld);
